import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Clock, 
  XCircle, 
  Mail, 
  Calendar, 
  Smile, 
  TrendingUp,
  MessageCircle,
  Bot,
  CheckCircle,
  Users,
  Smartphone,
  Shield,
  Zap,
  ArrowRight,
  Play,
  Castle,
  PartyPopper,
  ChevronRight,
  Heart,
  Sparkles
} from "lucide-react";

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        contact_name: name,
        phone,
        email,
        privacy_accepted: privacyAccepted,
        source: "resaflow-ai",
        submitted_at: new Date().toISOString(),
      };

      await fetch("https://hook.eu1.make.com/4sxs567uh2s631kahdvjxrgf5u3530em", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      // Show thank you popup
      setShowThankYou(true);
      
      // Reset form
      setName("");
      setEmail("");
      setPhone("");
      setPrivacyAccepted(false);
    } catch (error) {
      // Still show thank you (webhook likely received the data)
      setShowThankYou(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToDemo = () => {
    document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToHow = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Thank You Popup */}
      {showThankYou && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Blurred backdrop */}
          <div 
            className="absolute inset-0 bg-white/60 backdrop-blur-md"
            onClick={() => setShowThankYou(false)}
          />
          
          {/* Popup content */}
          <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-10 md:p-14 max-w-lg mx-4 text-center animate-in fade-in zoom-in duration-300">
            {/* Sparkles decoration */}
            <div className="absolute -top-6 left-1/2 -translate-x-1/2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Heart className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <div className="mt-6 mb-6">
              <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Merci pour votre demande !
              </h2>
              <p className="text-lg text-gray-600 mb-2">
                Nous avons bien reçu votre demande de démo.
              </p>
              <p className="text-gray-500">
                Notre équipe vous contactera dans les <span className="font-semibold text-cyan-600">24 heures</span> pour organiser votre démonstration personnalisée.
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-8">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Demande envoyée avec succès</span>
            </div>
            
            <Button 
              onClick={() => setShowThankYou(false)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-full px-10 h-14 text-lg font-semibold shadow-lg shadow-cyan-500/25"
            >
              <Heart className="w-5 h-5 mr-2" />
              Merci !
            </Button>
          </div>
        </div>
      )}
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              ResaFlow.ai
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={scrollToHow} className="text-gray-600 hover:text-gray-900 transition-colors">
              Comment ça marche
            </button>
            <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">
              Fonctionnalités
            </a>
            <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors">
              FAQ
            </a>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <a 
              href="/login"
              className="text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm md:text-base whitespace-nowrap"
            >
              Connexion
            </a>
            <Button 
              onClick={scrollToDemo}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-full px-3 sm:px-4 md:px-6 text-sm md:text-base h-9 md:h-10"
            >
              <span className="hidden sm:inline">Demander une démo</span>
              <span className="sm:hidden">Démo</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-cyan-50/50 via-white to-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 text-sm font-medium">
                <Castle className="w-4 h-4" />
                Pour les loueurs de structures gonflables
              </div>

              {/* Headlines */}
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Ne ratez plus aucune{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
                    réservation.
                  </span>
                </h1>
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-700">
                  Ne faites plus jamais de double réservation.
                </h2>
              </div>

              {/* Sub-headline */}
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                ResaFlow.ai automatise la gestion de vos structures gonflables :{" "}
                <span className="font-semibold text-gray-800">planning en temps réel</span> +{" "}
                <span className="font-semibold text-gray-800">agent IA qui répond à vos clients 24h/24</span>.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToDemo}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-full px-8 h-14 text-lg shadow-lg shadow-cyan-500/25"
                >
                  Demander une démo gratuite
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={scrollToHow}
                  variant="outline"
                  size="lg"
                  className="rounded-full px-8 h-14 text-lg border-2 border-gray-200 hover:border-cyan-300 hover:bg-cyan-50"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Voir comment ça fonctionne
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 pt-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Zéro double réservation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Réponses 24h/24</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Vous gardez le contrôle</span>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative lg:pl-8 lg:-mt-16">
              <div className="relative">
                {/* Main dashboard mockup */}
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 transform rotate-1">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-cyan-500 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Planning du jour</p>
                        <p className="text-sm text-gray-500">3 réservations confirmées</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {[
                        { name: "Marie D.", structure: "Mickey & Minnie", status: "Confirmé", color: "green" },
                        { name: "Pierre L.", structure: "Pat Patrouille", status: "En option", color: "yellow" },
                        { name: "Sophie M.", structure: "Licorne", status: "Demande", color: "cyan" },
                      ].map((item) => (
                        <div key={item.name} className="bg-white rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.structure}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            item.color === "green" ? "bg-green-100 text-green-700" :
                            item.color === "yellow" ? "bg-yellow-100 text-yellow-700" :
                            "bg-cyan-100 text-cyan-700"
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Chat bubble overlay - centered at bottom with overlap */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 max-w-[280px] transform rotate-1 z-10">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">ResaFlow.ai</span>
                        <br />
                        "Parfait ! Le château Mickey est disponible le 15/02. Je peux vous réserver le créneau après-midi ?"
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notification badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-4 py-2 shadow-lg animate-bounce">
                  <span className="text-sm font-medium">+3 nouvelles demandes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tagline Banner */}
      <section className="py-8 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-center text-white text-xl md:text-2xl font-medium">
            "Le planning qui travaille pendant que vous êtes sur le terrain."
          </p>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Conçu exclusivement pour les loueurs de structures gonflables
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
            ResaFlow.ai combine un <span className="font-semibold text-gray-800">outil de gestion des réservations et des disponibilités en temps réel</span> avec un <span className="font-semibold text-gray-800">agent IA capable de répondre automatiquement à toutes les demandes clients</span> sur vos réseaux sociaux.
          </p>
          
          {/* For who */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mt-12">
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="text-gray-700">Loueurs de structures gonflables</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="text-gray-700">Prestataires événementiels enfants</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="text-gray-700">Indépendants et petites équipes</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="text-gray-700">Ceux qui reçoivent beaucoup de messages</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Les bénéfices concrets
            </h2>
            <p className="text-lg text-gray-600">
              Tout ce que ResaFlow.ai va changer dans votre quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: "Gagnez plusieurs heures par semaine", desc: "Fini les réponses manuelles répétitives. L'IA s'en charge.", color: "cyan" },
              { icon: XCircle, title: "Zéro double réservation", desc: "Le système vérifie automatiquement les conflits.", color: "red" },
              { icon: Mail, title: "Aucune demande client oubliée", desc: "Toutes les demandes sont centralisées et tracées.", color: "blue" },
              { icon: Calendar, title: "Planning toujours à jour", desc: "Vue en temps réel de vos disponibilités.", color: "green" },
              { icon: Smile, title: "Moins de stress, plus de sérénité", desc: "Vous gérez sereinement, même le week-end.", color: "yellow" },
              { icon: TrendingUp, title: "Plus de réservations confirmées", desc: "Réponses rapides = meilleur taux de conversion.", color: "purple" },
            ].map((benefit) => (
              <Card key={benefit.title} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl mb-6 flex items-center justify-center transition-transform group-hover:scale-110 ${
                    benefit.color === "cyan" ? "bg-cyan-100" :
                    benefit.color === "red" ? "bg-red-100" :
                    benefit.color === "blue" ? "bg-blue-100" :
                    benefit.color === "green" ? "bg-green-100" :
                    benefit.color === "yellow" ? "bg-yellow-100" :
                    "bg-purple-100"
                  }`}>
                    <benefit.icon className={`w-7 h-7 ${
                      benefit.color === "cyan" ? "text-cyan-600" :
                      benefit.color === "red" ? "text-red-600" :
                      benefit.color === "blue" ? "text-blue-600" :
                      benefit.color === "green" ? "text-green-600" :
                      benefit.color === "yellow" ? "text-yellow-600" :
                      "text-purple-600"
                    }`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-gray-600">
              Un processus simple en 3 étapes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-20 left-1/4 right-1/4 h-1 bg-gradient-to-r from-cyan-300 via-blue-400 to-cyan-300" />

            {[
              { 
                step: "1", 
                icon: MessageCircle, 
                title: "Vos clients vous écrivent", 
                desc: "Sur Facebook Messenger (et bientôt d'autres canaux comme WhatsApp).",
                color: "cyan"
              },
              { 
                step: "2", 
                icon: Bot, 
                title: "L'agent IA répond", 
                desc: "Il renseigne, vérifie les disponibilités et collecte les informations nécessaires.",
                color: "blue"
              },
              { 
                step: "3", 
                icon: CheckCircle, 
                title: "Vous validez", 
                desc: "Les réservations arrivent en option dans votre planning. Vous confirmez quand tout est OK.",
                color: "green"
              },
            ].map((item) => (
              <div key={item.title} className="relative">
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center relative z-10 ${
                      item.color === "cyan" ? "bg-gradient-to-br from-cyan-400 to-cyan-600" :
                      item.color === "blue" ? "bg-gradient-to-br from-blue-400 to-blue-600" :
                      "bg-gradient-to-br from-green-400 to-green-600"
                    }`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-400">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Tool Features */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">L'outil (pour vous)</h3>
              </div>
              <div className="space-y-4">
                {[
                  "Planning clair par structure",
                  "Gestion des stocks et disponibilités",
                  "Réservations en option / confirmé / annulé",
                  "Accès rapide aux infos clients",
                  "Utilisable sur mobile et ordinateur",
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Features */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">L'IA (pour vos clients)</h3>
              </div>
              <div className="space-y-4">
                {[
                  "Répond 24h/24, même le dimanche",
                  "Pose les bonnes questions automatiquement",
                  "Ne promet jamais sans votre validation",
                  "Oriente vers vous si besoin",
                  "Vend sans forcer, de manière naturelle",
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-medium mb-8">
            <Shield className="w-4 h-4" />
            Ce qui nous différencie
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Pensé exclusivement pour les loueurs de structures gonflables
          </h2>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed">
            Contrairement aux outils génériques, ResaFlow.ai comprend votre métier.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: XCircle, text: "Pas d'usine à gaz", subtext: "Simple et efficace" },
              { icon: Shield, text: "Pas de promesses automatiques risquées", subtext: "Validation humaine obligatoire" },
              { icon: Users, text: "Vous gardez toujours la main", subtext: "Contrôle total" },
            ].map((item) => (
              <div key={item.text} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <item.icon className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
                <p className="text-white font-semibold mb-1">{item.text}</p>
                <p className="text-gray-400 text-sm">{item.subtext}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Case Study Teaser */}
      <section className="py-20 px-4 bg-cyan-50">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12 bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
                  <div className="flex items-center gap-3 mb-6">
                    <PartyPopper className="w-8 h-8" />
                    <span className="text-lg font-semibold">Cas client</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Gonflable 974
                  </h3>
                  <p className="text-cyan-100 mb-6">
                    Loueur de structures gonflables à La Réunion
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>6 structures gérées</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>0 double réservation depuis l'installation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>-50% de temps passé en gestion</span>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <blockquote className="text-xl text-gray-700 italic mb-6">
                    "Depuis que j'utilise ResaFlow.ai, je ne m'inquiète plus des doubles réservations. Le chatbot répond aux clients même quand je suis en livraison."
                  </blockquote>
                  <p className="text-gray-500">— Gérant, Gonflable 974</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Questions fréquentes
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: "Est-ce que l'IA confirme les réservations toute seule ?",
                a: "Non, jamais. L'IA collecte les informations et crée des demandes en \"option\". Vous gardez le contrôle total et validez manuellement chaque réservation."
              },
              {
                q: "Puis-je gérer plusieurs structures ?",
                a: "Oui, absolument. Chaque structure a sa propre disponibilité et son propre planning. Vous pouvez en ajouter autant que nécessaire."
              },
              {
                q: "Est-ce compliqué à mettre en place ?",
                a: "Non. L'installation prend moins d'une heure et nous vous accompagnons à chaque étape. Votre catalogue et vos premières structures sont configurés avec vous."
              },
              {
                q: "Est-ce adapté aux packs festifs (anniversaires, etc.) ?",
                a: "Oui, les packs festifs sont gérés avec une validation humaine obligatoire. L'IA ne confirme jamais un pack sans votre accord explicite."
              },
              {
                q: "Combien ça coûte ?",
                a: "Nous proposons plusieurs formules adaptées à votre volume d'activité. Demandez une démo gratuite pour découvrir l'offre qui vous correspond."
              },
            ].map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`} className="bg-white rounded-xl border shadow-sm px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-6">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="demo" className="py-20 px-4 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Reprenez le contrôle de vos réservations dès maintenant
          </h2>
          <p className="text-xl text-cyan-100 mb-10">
            Demandez une démo gratuite et découvrez comment ResaFlow.ai peut transformer votre activité.
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Demander une démo gratuite</h3>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Votre nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
              />
              <input
                type="email"
                placeholder="Votre email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
              />
              <input
                type="tel"
                placeholder="Votre téléphone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
              />
              
              {/* Privacy checkbox */}
              <label className="flex items-start gap-3 cursor-pointer text-left">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600">
                  J'accepte les{" "}
                  <a 
                    href="https://ai-success-lab.pulsia.ai/mentions-legales-politique-de-confidentialite/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-700 underline"
                  >
                    conditions générales et la politique de confidentialité
                  </a>
                  {" "}de Pulsia.ai
                </span>
              </label>

              <Button 
                type="submit"
                disabled={isSubmitting || !privacyAccepted}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-xl h-14 text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Demander ma démo gratuite
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
            <p className="text-sm text-gray-500 mt-4">
              Sans engagement • Réponse sous 24h
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">ResaFlow.ai</span>
            </div>
            <p className="text-gray-400 text-sm">
              Le flux de vos réservations, enfin sous contrôle.
            </p>
            <p className="text-gray-500 text-sm">
              © 2025 ResaFlow.ai • Tous droits réservés
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
